import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import Immutable from 'immutable';
import { transactionPayloadSelector, pageServerTimeSelector } from 'ctm-selectors';
import { pageContext, fetchServerTime, convertEpochToLocalDateString } from 'ctm-util-functions';

import { experimentIDEnum } from 'ctm-vertical-base/base/reducers/page';
import { Button } from '../button/button';
import { Icon } from '../icon/icon';
import { SimpleFlexbox } from '../flexbox/flexbox';

import styles from './dev_tool.css';
import { Label } from '../form';

const _DevTool = (props) => {
    const dispatch = useDispatch();
    const [galaxyMigrationPhaseOneFlag, setGalaxyMigrationPhaseOneFlag] = React.useState(
        sessionStorage.getItem('GalaxyMigrationPhaseOneFlag') || 'N'
    );
    // Tracks that a reload is due once the pending flag-change render has committed, rather than
    // guessing at a fixed delay (e.g. setTimeout) for setState to flush first.
    const reloadPendingRef = React.useRef(false);

    const switchTheme = (newTheme) => {
        // For CTM rebrand, only set the experiment ID
        // The useBrandingService hook will detect this and switch to 'ctmrebrand' theme
        if (newTheme === 'local') {
            window.runExperiment(experimentIDEnum.CTM_REBRAND);
            return;
        }

        // For other themes, clear the experiment and set the theme directly
        window.runExperiment(null);
        dispatch({
            type: 'SET_THEME',
            theme: newTheme
        });
    };

    /**
     * Set serverTime in the store from server_time api express endpoint
     */
    const updateServerTime = () => {
        fetchServerTime((response) => {
            const serverTime = convertEpochToLocalDateString(response);
            dispatch({ type: 'SET_PAGE_VALUE', identifier: 'serverTime', value: serverTime });
        });
    };

    useEffect(() => {
        if (galaxyMigrationPhaseOneFlag === 'Y') {
            window.runExperiment(experimentIDEnum.GALAXY_MIGRATION_PHASE_ONE);
        }
    }, [galaxyMigrationPhaseOneFlag]);

    // Runs after the flag-change render has committed, so the reload always happens once
    // React has finished flushing state - no arbitrary delay required.
    useEffect(() => {
        if (reloadPendingRef.current) {
            reloadPendingRef.current = false;
            window.location.reload();
        }
    }, [galaxyMigrationPhaseOneFlag]);

    const setGalaxyMigrationPhaseOneFlagToSessionStorage = (value) => {
        if (value !== galaxyMigrationPhaseOneFlag) {
            sessionStorage.setItem('GalaxyMigrationPhaseOneFlag', value);
            reloadPendingRef.current = true;
            setGalaxyMigrationPhaseOneFlag(value);
        }
    };
    /**
     * Renders the ServerTime Toolbar
     * @returns {Node} The serverTime Toolbar
     */
    const renderServerToolbar = () => {
        const { clientTime, serverTime } = props;
        const client = clientTime || new Date();
        return (
            <SimpleFlexbox display="flex" padding="all" contentBetween="xs" itemsCenter="xs" border="bottom">
                <div>
                    <Icon variant="ctm_calendar" margin="right" />
                    {`Server Time: ${serverTime}`}
                </div>
                <Button
                    label="Update Time"
                    UIComponent="btn_primary_transparent_small_rounded"
                    onClick={updateServerTime}
                />
                <div>
                    <Icon variant="ctm_calendar" margin="right" />
                    {`Client Time: ${client.toString()}`}
                </div>
            </SimpleFlexbox>
        );
    };

    /**
     * Renders the Journey Toolbar
     * @returns {Node} The serverTime Toolbar
     */
    const renderJourneyToolbar = () => {
        const { journeyId, accountId } = props;
        const journeyIdString = journeyId ? journeyId.toString() : '';
        const accountIdString = accountId ? accountId.toString() : '';
        return (
            <>
                <SimpleFlexbox className={styles.row} wrap display="flex" padding="all" contentBetween="xs" itemsCenter="xs">
                    <span>{`Journey ID: ${journeyIdString}`}</span>
                    <span>{`Account ID: ${accountIdString}`}</span>
                    <Button
                        href={`https://github.com/isuctm/ctm-enterprise-schematic/commit/${process.env.appVersion}`}
                        target="_blank"
                        UIComponent="btn_primary_transparent_small_rounded"
                    >
                        {`Platform Commit: ${process.env.appVersion}`}
                    </Button>
                    <SimpleFlexbox className={styles.row} wrap display="flex" padding="all" contentBetween="xs" itemsCenter="xs">
                        <Button
                            label="ctm"
                            UIComponent="btn_primary_transparent_small_rounded"
                            onClick={() => switchTheme('ctm')}
                        />
                        <Button
                            label="choo"
                            UIComponent="btn_primary_transparent_small_rounded"
                            onClick={() => switchTheme('choo')}
                        />
                        <Button
                            label="islt"
                            UIComponent="btn_primary_transparent_small_rounded"
                            onClick={() => switchTheme('islt')}
                        />
                        <Button
                            label="ctm-rebrand"
                            UIComponent="btn_primary_transparent_small_rounded"
                            onClick={() => switchTheme('local')}
                        />
                    </SimpleFlexbox>
                    <SimpleFlexbox>
                        <Label>
                            <input
                                type="checkbox"
                                id="galaxy-migration-phase-one-flag"
                                checked={galaxyMigrationPhaseOneFlag === 'Y'}
                                onChange={(e) => setGalaxyMigrationPhaseOneFlagToSessionStorage(e.target.checked ? 'Y' : 'N')}
                            />
                             Galaxy Migration Phase 1
                        </Label>
                    </SimpleFlexbox>
                </SimpleFlexbox>
            </>
        );
    };
    return (
        pageContext.inDevMode() ? (
            <SimpleFlexbox containerFluid bgColor="body_background" border="top">
                {renderServerToolbar()}
                {renderJourneyToolbar()}
            </SimpleFlexbox>
        ) : null
    );
};

_DevTool.propTypes = {
    journeyId: PropTypes.number,
    accountId: PropTypes.number,
    serverTime: PropTypes.string,
    clientTime: PropTypes.string
};

const mapStateToProps = (state) => {
    const transaction = transactionPayloadSelector(state) || Immutable.Map();
    const serverTime = pageServerTimeSelector(state);
    return {
        journeyId: transaction.getIn(['journeyId']),
        accountId: transaction.getIn(['accountId']),
        serverTime
    };
};

const DevTool = connect(mapStateToProps)(_DevTool);
export { DevTool, _DevTool };
